import { Fragment, useState, useEffect, useMemo } from 'react';
import {
  useListAdmins,
  useCreateAdmin,
  useUpdateAdmin,
  useDeleteAdmin,
  type AdminSummary,
  useSuperAdminListAdminUsers,
  useSuperAdminCreateUser,
  useSuperAdminDeleteUser,
  useSuperAdminGetUserPermissions,
  useSuperAdminUpdateUserPermissions,
  useUpdateAdminEmailVerification,
} from '@/hooks/useRBAC';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Plus,
  Target,
  Users,
  Shield,
  Trash2,
  Search,
  List as ListIcon,
  Table as TableIcon,
  Grid3x3,
  Mail,
  Phone,
  MapPin,
  Map,
  Edit,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Building2,
  Briefcase,
  ArrowUp,
  ArrowDown,
  ChevronsUpDown,
  Clock,
} from 'lucide-react';
import { useAlert } from '@/components/AlertProvider';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { ModuleHeader } from '@/components/layout/ModuleHeader';
import { AddButton } from '@/components/ui/AddButton';
import { PhoneInput } from '@/components/ui/PhoneInput';
import { cn } from '@/utils/cn';
import api from '@/services/api';

type CreateAdminTab = 'apporteur' | 'entreprise' | 'bancaire' | 'utilisateurs';
type SortColumn = 'last_name' | 'first_name' | 'email' | 'mobile_phone' | 'city' | 'role' | 'is_active';

function SortIcon({ column, sortColumn, sortDirection }: { column: SortColumn; sortColumn: SortColumn | null; sortDirection: 'asc' | 'desc' }) {
  if (sortColumn !== column) {
    return <ChevronsUpDown className="h-3 w-3 ml-1 opacity-40 inline" />;
  }
  return sortDirection === 'asc'
    ? <ArrowUp className="h-3 w-3 ml-1 text-blue-600 inline" />
    : <ArrowDown className="h-3 w-3 ml-1 text-blue-600 inline" />;
}

function AdminUsersManager({ adminId, admin }: { adminId: number; admin: AdminSummary }) {
  const { data: summary, isLoading, refetch } = useSuperAdminListAdminUsers(adminId);
  const createUser = useSuperAdminCreateUser(adminId);
  const deleteUser = useSuperAdminDeleteUser(adminId);
  const updatePerm = useSuperAdminUpdateUserPermissions(adminId);
  const { showSuccess, showError } = useAlert();
  const users = summary?.users ?? [];

  const initials = (admin.first_name?.[0] || admin.email?.[0] || 'A').toUpperCase() + (admin.last_name?.[0] || '').toUpperCase();

  const label = admin.role === 'super_admin' ? 'super administrateur' : admin.role === 'admin' ? 'administrateur' : 'apporteur';

  const [form, setForm] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    can_delete: false,
  });
  const [createOpen, setCreateOpen] = useState(false);

  const handleCreate = async () => {
    try {
      await createUser.mutateAsync({
        email: form.email.trim(),
        password: form.password,
        first_name: form.first_name || undefined,
        last_name: form.last_name || undefined,
        can_delete: form.can_delete,
      });
      setForm({ email: '', password: '', first_name: '', last_name: '', can_delete: false });
      setCreateOpen(false);
      showSuccess(`Utilisateur ajouté sous cet ${label}`);
    } catch (e: any) {
      showError(e?.response?.data?.detail || 'Création impossible');
    }
  };

  const handleTogglePerm = async (userId: number, current: boolean) => {
    try {
      await updatePerm.mutateAsync({ user_id: userId, can_delete: !current });
      showSuccess('Permissions mises à jour');
    } catch (e: any) {
      showError(e?.response?.data?.detail || 'Mise à jour impossible');
    }
  };

  const handleDelete = async (userId: number) => {
    try {
      await deleteUser.mutateAsync(userId);
      showSuccess('Utilisateur supprimé avec succès');
    } catch (e: any) {
      showError(e?.response?.data?.detail || 'Suppression impossible');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Users className="h-4 w-4" />
            <span className="hidden sm:inline">Utilisateurs de l'{label}</span>
            <span className="sm:hidden">Utilisateurs</span>
          </div>
          <div className="flex items-center gap-2 rounded-full bg-muted px-3 py-1">
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-[10px] font-semibold text-white">
              {initials || 'AD'}
            </div>
            <div className="text-sm leading-tight">
              <div className="font-medium">{[admin.first_name, admin.last_name].filter(Boolean).join(' ') || (admin.role === 'super_admin' ? 'Super Admin' : admin.role === 'admin' ? 'Administrateur' : 'Apporteur')}</div>
              <div className="text-xs text-muted-foreground truncate max-w-[200px] sm:max-w-none">{admin.email}</div>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button size="sm" variant="outline" onClick={() => refetch()} className="w-full sm:w-auto">
            <RefreshCw className="mr-2 h-4 w-4" />
            Actualiser
          </Button>
          <Button size="sm" onClick={() => setCreateOpen(true)} className="w-full sm:w-auto">
            <Plus className="mr-2 h-4 w-4" /> Créer un utilisateur
          </Button>
        </div>
      </div>

      {/* Dialog création utilisateur */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Créer un utilisateur</DialogTitle>
            <DialogDescription>Renseignez les informations de l'utilisateur.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Email de l'utilisateur</label>
              <Input placeholder="ex: user@exemple.com" type="email" autoComplete="off" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Mot de passe</label>
              <Input placeholder="••••••••" type="password" autoComplete="new-password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Prénom</label>
              <Input placeholder="Prénom" value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Nom</label>
              <Input placeholder="Nom" value={form.last_name} onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))} />
            </div>
            <div className="flex items-center gap-2 pt-2 sm:col-span-2">
              <Switch checked={form.can_delete} onCheckedChange={(v) => setForm((f) => ({ ...f, can_delete: Boolean(v) }))} />
              <span className="text-sm">Peut supprimer</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Annuler</Button>
            <Button onClick={handleCreate} disabled={createUser.isPending}>
              <Plus className="mr-2 h-4 w-4" /> Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {summary && (
        <div className="rounded-md border bg-background p-3">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs">
              <span className="h-2 w-2 rounded-full bg-blue-500" />
              Quota: {summary.max_users_quota}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs">
              <span className="h-2 w-2 rounded-full bg-green-500" />
              Utilisés: {summary.users_count}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs">
              <span className="h-2 w-2 rounded-full bg-amber-500" />
              Restants: {summary.remaining}
            </span>
          </div>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-blue-600 transition-all"
              style={{ width: `${Math.min(100, summary.max_users_quota ? (summary.users_count / Math.max(1, summary.max_users_quota)) * 100 : 0)}%` }}
            />
          </div>
        </div>
      )}

      {/* Inline form removed as requested */}

      <div className="bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-4 text-sm text-muted-foreground">Chargement des utilisateurs…</div>
        ) : users.length === 0 ? (
          <div className="p-4 text-sm text-muted-foreground">Aucun utilisateur sous cet {label}.</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20 border-b-2 border-blue-200 dark:border-blue-800">
                <TableRow>
                  <TableHead className="min-w-[120px]">Nom</TableHead>
                  <TableHead className="min-w-[200px]">Email</TableHead>
                  <TableHead className="min-w-[80px]">Actif</TableHead>
                  <TableHead className="min-w-[120px]">Peut supprimer</TableHead>
                  <TableHead className="min-w-[120px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <AdminUserRow
                    key={u.id}
                    adminId={adminId}
                    user={u}
                    onTogglePerm={handleTogglePerm}
                    onDelete={handleDelete}
                    updatingPerm={updatePerm.isPending}
                    deleting={deleteUser.isPending}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}

function AdminUserRow({
  adminId,
  user,
  onTogglePerm,
  onDelete,
  updatingPerm,
  deleting,
}: {
  adminId: number;
  user: any;
  onTogglePerm: (userId: number, current: boolean) => Promise<void> | void;
  onDelete: (userId: number) => Promise<void> | void;
  updatingPerm: boolean;
  deleting: boolean;
}) {
  const perms = useSuperAdminGetUserPermissions(adminId, user.id);
  const canDelete = perms.data?.can_delete ?? false;
  const loadingPerm = perms.isLoading;

  return (
    <TableRow>
      <TableCell>{[user.first_name, user.last_name].filter(Boolean).join(' ') || '—'}</TableCell>
      <TableCell>{user.email}</TableCell>
      <TableCell>{user.is_active ? 'Actif' : 'Inactif'}</TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Switch checked={canDelete} onCheckedChange={() => onTogglePerm(user.id, canDelete)} disabled={loadingPerm || updatingPerm} />
          {loadingPerm && <span className="text-xs text-muted-foreground">Chargement…</span>}
        </div>
      </TableCell>
      <TableCell>
        <Button size="sm" variant="destructive" onClick={() => onDelete(user.id)} disabled={deleting} className="whitespace-nowrap">
          <Trash2 className="mr-2 h-4 w-4" />
          <span className="hidden sm:inline">Supprimer</span>
          <span className="sm:hidden">Supp.</span>
        </Button>
      </TableCell>
    </TableRow>
  );
}

export default function SuperAdminAdmins({ defaultRoleFilter, title }: { defaultRoleFilter?: 'admin' | 'apporteur' | 'super_admin', title?: string }) {
  const buildAddressComplete = (address: string, postalCode: string, city: string, country?: string) => {
    return [address, postalCode, city, country]
      .map((v) => (typeof v === 'string' ? v.trim() : ''))
      .filter(Boolean)
      .join(' ');
  };

  const buildMapsLink = (addressComplete: string) => {
    const trimmed = addressComplete.trim();
    return trimmed ? `https://www.google.com/maps/search/${encodeURIComponent(trimmed)}` : '';
  };

  const buildMapsEmbedSrc = (addressComplete: string) => {
    const trimmed = addressComplete.trim();
    return trimmed
      ? `https://www.google.com/maps?q=${encodeURIComponent(trimmed)}&output=embed`
      : '';
  };

  const [search, setSearch] = useState('');
  const [statusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [commissionFilter] = useState<'all' | 'with' | 'without'>('all');
  const [roleFilter] = useState<'all' | 'admin' | 'apporteur' | 'super_admin'>('all');
  const effectiveRoleFilter = (() => {
    // When defaultRoleFilter is 'admin', don't send a role filter to API
    // This allows the API to return all admin-type users (admin + super_admin)
    // The frontend filtering will then exclude apporteurs
    if (defaultRoleFilter === 'admin') {
      // If a specific role is selected in the dropdown filter, use it
      return roleFilter !== 'all' ? roleFilter : undefined;
    }
    // For apporteur tab or other cases, use the defaultRoleFilter or selected roleFilter
    return defaultRoleFilter || (roleFilter !== 'all' ? roleFilter : undefined);
  })();

  const { data: admins = [], isLoading } = useListAdmins({
    search: search.trim() || undefined,
    status: statusFilter,
    commissions: commissionFilter,
    role: effectiveRoleFilter,
  });
  const createAdmin = useCreateAdmin();
  const updateAdmin = useUpdateAdmin();
  const { showSuccess, showError } = useAlert();
  const deleteAdmin = useDeleteAdmin();
  const updateAdminEmailVerification = useUpdateAdminEmailVerification();

  const [showCreate, setShowCreate] = useState(false);
  const [showMapPreview, setShowMapPreview] = useState(false);
  const [createForm, setCreateForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: defaultRoleFilter === 'admin' ? 'admin' : 'apporteur',
    mobile_phone: '',
    company_name: '',
    siret: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'France',
    maps_link: '',
    commissions_enabled: 'no',
    is_active: true,
    is_email_verified: true,
    bank_name: '',
    bank_account_holder: '',
    iban: '',
    bic: '',
    marques: [] as string[],
  });

  const [activeCreateTab, setActiveCreateTab] = useState<CreateAdminTab>(roleFilter === 'admin' ? 'apporteur' : 'apporteur');

  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'table'>(window.innerWidth < 640 ? 'grid' : 'table');
  const [columnFilters, setColumnFilters] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    siret: '',
    role: '',
    status: '',
    brand: '',
  });

  const [editingAdmin, setEditingAdmin] = useState<AdminSummary | null>(null);
  const [brands, setBrands] = useState<{ id: number; name: string; is_active: boolean }[]>([]);
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Normalisation stricte pour les comparaisons de chaînes (marques)
  const normalize = (s: string) => String(s).toLowerCase().replace(/[^a-z0-9]/g, '');

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const res = await api.get('/product-config/brands');
        setBrands(res.data.filter((b: any) => b.is_active));
      } catch (e) {
        console.error('Failed to fetch brands', e);
      }
    };
    fetchBrands();
  }, []);

  // Synchroniser les marques quand editingAdmin change
  useEffect(() => {
    if (editingAdmin) {
      const marquesValue = Array.isArray(editingAdmin.marques) ? [...editingAdmin.marques] : [];
      console.log('[useEffect] Syncing marques from editingAdmin:', marquesValue);
      setCreateForm(prev => ({
        ...prev,
        marques: marquesValue,
      }));
    }
  }, [editingAdmin]);

  const resetForm = () => {
    setActiveCreateTab('apporteur');
    setShowMapPreview(false);
    setCreateForm({
      email: '',
      first_name: '',
      last_name: '',
      role: defaultRoleFilter === 'admin' ? 'admin' : 'apporteur',
      mobile_phone: '',
      company_name: '',
      siret: '',
      address: '',
      city: '',
      postal_code: '',
      country: 'France',
      maps_link: '',
      commissions_enabled: 'no',
      is_active: true,
      is_email_verified: true,
      bank_name: '',
      bank_account_holder: '',
      iban: '',
      bic: '',
      marques: [],
    });
  };

  const handleEdit = (admin: AdminSummary) => {
    console.log('[handleEdit] admin object:', admin);
    console.log('[handleEdit] admin.marques:', admin.marques);
    const marquesValue = Array.isArray(admin.marques) ? [...admin.marques] : [];
    console.log('[handleEdit] marquesValue:', marquesValue);
    setShowMapPreview(false);
    setEditingAdmin(admin);
    setActiveCreateTab('apporteur');
    setCreateForm({
      email: admin.email || '',
      first_name: admin.first_name || '',
      last_name: admin.last_name || '',
      role: (admin as any).role || 'apporteur',
      mobile_phone: admin.mobile_phone || '',
      company_name: admin.company_name || '',
      siret: admin.siret || '',
      address: admin.address || '',
      city: admin.city || '',
      postal_code: admin.postal_code || '',
      country: admin.country || 'France',
      maps_link: admin.maps_link || '',
      commissions_enabled: admin.commissions_enabled || 'no',
      is_active: admin.is_active,
      is_email_verified: admin.is_email_verified !== false,
      bank_name: admin.bank_name || '',
      bank_account_holder: admin.bank_account_holder || '',
      iban: admin.iban || '',
      bic: admin.bic || '',
      marques: marquesValue,
    });
    setShowCreate(true);
  };

  const handleSaveAdmin = async () => {
    try {
      if (createForm.role === 'admin') {
        if (!createForm.email.trim()) throw new Error("L'email est requis");
        if (!createForm.first_name.trim()) throw new Error("Le prénom est requis");
        if (!createForm.last_name.trim()) throw new Error("Le nom est requis");
        // Le téléphone mobile n'est plus obligatoire pour un admin
      }

      const addressParts = [createForm.address, createForm.postal_code, createForm.city, createForm.country].filter(Boolean).join(' ');
      const maps_link = createForm.maps_link || (addressParts ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressParts)}` : undefined);

      const basePayload = {
        email: createForm.email.trim(),
        first_name: createForm.first_name || undefined,
        last_name: createForm.last_name || undefined,
        role: createForm.role,
        mobile_phone: createForm.mobile_phone || undefined,
        company_name: createForm.company_name || undefined,
        siret: createForm.siret || undefined,
        address: createForm.address || undefined,
        city: createForm.city || undefined,
        postal_code: createForm.postal_code || undefined,
        country: createForm.country || undefined,
        maps_link,
        commissions_enabled: createForm.commissions_enabled,
        is_active: createForm.is_active,
        bank_name: createForm.bank_name || undefined,
        bank_account_holder: createForm.bank_account_holder || undefined,
        iban: createForm.iban || undefined,
        bic: createForm.bic || undefined,
        marques: createForm.role === 'apporteur' && createForm.marques.length > 0 ? createForm.marques : undefined,
      };

      if (editingAdmin) {
        const trimmedEmail = createForm.email.trim();
        await updateAdmin.mutateAsync({
          admin_id: editingAdmin.id,
          ...basePayload,
          ...(trimmedEmail ? { email: trimmedEmail } : {}),
        });

        // Update email verification if changed
        if (createForm.is_email_verified !== (editingAdmin.is_email_verified !== false)) {
          await updateAdminEmailVerification.mutateAsync({
            admin_id: editingAdmin.id,
            is_email_verified: createForm.is_email_verified,
          });
        }

        showSuccess(createForm.role === 'admin' ? "Administrateur mis à jour avec succès" : "Apporteur mis à jour avec succès");
      } else {
        const payload: any = {
          ...basePayload,
          email: createForm.email.trim(),
        };

        await createAdmin.mutateAsync(payload);
        setSearch('');
        const roleLabel = createForm.role === 'super_admin' ? "super administrateur" : createForm.role === 'admin' ? "administrateur" : "apporteur";
        showSuccess(`Le nouvel ${roleLabel} a été créé avec succès`);
      }

      resetForm();
      setEditingAdmin(null);
      setShowCreate(false);
    } catch (e: any) {
      showError(e?.response?.data?.detail || 'Enregistrement impossible');
    }
  };

  // Quota update functionality removed - editQuota state not used

  const filteredAdmins = admins.filter((a) => {
    if (statusFilter === 'active' && !a.is_active) return false;
    if (statusFilter === 'inactive' && a.is_active) return false;
    if (commissionFilter === 'with' && !a.commissions_enabled) return false;
    if (commissionFilter === 'without' && a.commissions_enabled) return false;

    // When on admin tab (defaultRoleFilter === 'admin'), show admin and super_admin, exclude apporteurs
    if (defaultRoleFilter === 'admin' && a.role === 'apporteur') return false;

    // When on apporteur tab (defaultRoleFilter === 'apporteur'), show only apporteurs
    if (defaultRoleFilter === 'apporteur' && a.role !== 'apporteur') return false;

    // Column filters
    if (columnFilters.name) {
      const term = columnFilters.name.toLowerCase();
      const fullName = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase();
      if (!fullName.includes(term) && !a.company_name?.toLowerCase().includes(term)) return false;
    }
    if (columnFilters.email && !a.email?.toLowerCase().includes(columnFilters.email.toLowerCase())) return false;
    if (columnFilters.phone && !a.mobile_phone?.includes(columnFilters.phone)) return false;
    if (columnFilters.city && !a.city?.toLowerCase().includes(columnFilters.city.toLowerCase())) return false;
    if (columnFilters.siret && !a.siret?.includes(columnFilters.siret)) return false;
    if (columnFilters.role) {
      if (!a.role?.toLowerCase().includes(columnFilters.role.toLowerCase())) return false;
    }
    if (columnFilters.status) {
      const isActive = columnFilters.status === 'actif';
      if (a.is_active !== isActive) return false;
    }
    if (columnFilters.brand) {
      if (!a.marques) return false;
      const filterNorm = normalize(columnFilters.brand);
      const hasMatch = a.marques.some(m => {
        const mNorm = normalize(m);
        return mNorm === filterNorm;
      });
      if (!hasMatch) return false;
    }

    return true;
  });

  const getBrandDisplayName = (brandValue: string) => {
    const normalize = (s: string) => String(s).toLowerCase().replace(/[^a-z0-9]/g, '');
    const valNorm = normalize(brandValue);
    const found = brands.find(b => normalize(b.name) === valNorm);
    return found ? found.name : brandValue;
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortedAdmins = useMemo(() => {
    if (!sortColumn) return filteredAdmins;

    return [...filteredAdmins].sort((a, b) => {
      let aValue: string | boolean | null | undefined;
      let bValue: string | boolean | null | undefined;

      if (sortColumn === 'is_active') {
        aValue = a.is_active;
        bValue = b.is_active;
        const aNum = aValue ? 1 : 0;
        const bNum = bValue ? 1 : 0;
        return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
      } else {
        aValue = a[sortColumn] ?? '';
        bValue = b[sortColumn] ?? '';
        const comparison = String(aValue).localeCompare(String(bValue), 'fr', { sensitivity: 'base' });
        return sortDirection === 'asc' ? comparison : -comparison;
      }
    });
  }, [filteredAdmins, sortColumn, sortDirection]);

  const handleToggleStatus = async (admin: AdminSummary) => {
    try {
      const newStatus = !admin.is_active;
      await updateAdmin.mutateAsync({
        admin_id: admin.id,
        is_active: newStatus,
      });
      const label = admin.role === 'admin' ? "Administrateur" : "Apporteur";
      showSuccess(newStatus ? `${label} activé` : `${label} désactivé`);
    } catch (e: any) {
      showError(e?.response?.data?.detail || "Erreur lors de la modification du statut");
    }
  };

  return (
    <div className="md:h-full md:min-h-0 flex flex-col gap-3 sm:gap-6">
      <ModuleHeader
        icon={Target}
        title={title || (defaultRoleFilter === 'admin' ? "Administrateurs" : "Apporteurs d'Affaires")}
        subtitle={defaultRoleFilter === 'admin' ? "Gérez les comptes administrateurs de la plateforme." : "Créez et gérez vos apporteurs d'affaires et les comptes associés."}
        actions={
          <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto flex-wrap">
            <div className="relative flex-1 min-w-0 sm:flex-none sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                id="admin-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher..."
                className="pl-10 h-9 sm:h-10 text-sm"
                autoComplete="off"
              />
            </div>
            <div className="flex items-center justify-center gap-0.5 sm:gap-1 border border-slate-300 dark:border-slate-600 rounded-lg p-0.5 sm:p-1">
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                className={`p-1.5 sm:p-2 rounded ${viewMode === 'grid'
                  ? 'bg-purple-100 dark:bg-purple-950/30 text-purple-700'
                  : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'
                  }`}
              >
                <Grid3x3 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className={`p-1.5 sm:p-2 rounded ${viewMode === 'list'
                  ? 'bg-purple-100 dark:bg-purple-950/30 text-purple-700'
                  : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'
                  }`}
              >
                <ListIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode('table')}
                className={`hidden sm:block p-1.5 sm:p-2 rounded ${viewMode === 'table'
                  ? 'bg-purple-100 dark:bg-purple-950/30 text-purple-700'
                  : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'
                  }`}
              >
                <TableIcon className="h-4 w-4" />
              </button>
            </div>
            <AddButton
              label={defaultRoleFilter === 'admin' ? "Nouvel Administrateur" : "Nouvel Apporteur"}
              onClick={() => {
                setEditingAdmin(null);
                resetForm();
                setShowMapPreview(false);
                setShowCreate(true);
              }}
              className="w-full sm:w-auto"
            />
          </div>
        }
      />

      <Dialog
        open={showCreate}
        onOpenChange={(open) => {
          setShowCreate(open);
          if (!open) {
            setEditingAdmin(null);
            resetForm();
          }
        }}
      >
        <DialogContent className="max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-2xl p-0">
          <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700 sticky top-0 bg-white dark:bg-slate-900 z-10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                <Users className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                  {editingAdmin
                    ? (createForm.role === 'super_admin' ? "Modifier le super administrateur" : createForm.role === 'admin' ? "Modifier l'administrateur" : "Modifier l'apporteur")
                    : (createForm.role === 'super_admin' ? "Nouveau Super Administrateur" : createForm.role === 'admin' ? "Nouvel Administrateur" : "Nouvel Apporteur")}
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">Les champs marqués d'un * sont obligatoires</p>
              </div>
            </div>
            <button
              onClick={() => setShowCreate(false)}
              className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            >
              ✕
            </button>
          </div>
          <div className="p-6 space-y-6">
            {roleFilter !== 'admin' && createForm.role === 'apporteur' && (
              <div className="flex gap-1 mb-4 bg-slate-100 dark:bg-slate-800 p-1 rounded-xl">
                {([
                  { id: 'apporteur' as CreateAdminTab, label: "Apporteur d'affaires", icon: Users },
                  { id: 'entreprise' as CreateAdminTab, label: 'Entreprise', icon: Building2 },
                  { id: 'bancaire' as CreateAdminTab, label: 'Informations bancaires', icon: Briefcase },
                ] as const).map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveCreateTab(tab.id)}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${activeCreateTab === tab.id
                      ? 'bg-white dark:bg-slate-900 text-purple-600 shadow-sm'
                      : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                      }`}
                  >
                    <tab.icon className="h-4 w-4" />
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                ))}
              </div>
            )}

            {activeCreateTab === 'apporteur' && (
              <>
                <div className="space-y-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/40 p-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-100">
                    <Users className="h-4 w-4 text-purple-500" />
                    <span>Informations personnelles</span>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-1 sm:col-span-2">
                      <label className="block text-sm font-medium mb-1">Email *</label>
                      <Input
                        placeholder={createForm.role === 'admin' ? "ex: admin@example.com" : "ex: apporteur@example.com"}
                        type="email"
                        autoComplete="off"
                        value={createForm.email}
                        onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-1">
                      {editingAdmin && editingAdmin.is_email_verified ? (
                        <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                          <p className="text-xs text-green-700 dark:text-green-300 flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4" />
                            <span><strong>Email vérifié :</strong> L'utilisateur a validé son adresse email et activé son compte.</span>
                          </p>
                        </div>
                      ) : (
                        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                          <p className="text-xs text-blue-700 dark:text-blue-300">
                            <strong>✉️ Validation par email :</strong> {editingAdmin ? "L'email n'a pas encore été vérifié. L'utilisateur doit valider son email pour activer son compte." : "Un email de vérification sera envoyé à l'adresse fournie. L'utilisateur devra valider son email et définir son mot de passe pour activer son compte."}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-1">
                      <label className="block text-sm font-medium mb-1">Prénom {createForm.role === 'admin' ? '*' : ''}</label>
                      <Input
                        placeholder="Prénom"
                        value={createForm.first_name}
                        onChange={(e) => setCreateForm((f) => ({ ...f, first_name: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="block text-sm font-medium mb-1">Nom {createForm.role === 'admin' ? '*' : ''}</label>
                      <Input
                        placeholder="Nom"
                        value={createForm.last_name}
                        onChange={(e) => setCreateForm((f) => ({ ...f, last_name: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="block text-sm font-medium mb-1">Téléphone mobile {createForm.role === 'admin' ? '' : '*'}</label>
                    <PhoneInput
                      value={createForm.mobile_phone}
                      onChange={(value) => setCreateForm((f) => ({ ...f, mobile_phone: value }))}
                      className="w-full"
                    />
                  </div>
                </div>

                {createForm.role === 'apporteur' && (
                  <>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium mb-1">Statut</label>
                        <select
                          className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-sm"
                          value={createForm.is_active ? 'active' : 'inactive'}
                          onChange={(e) =>
                            setCreateForm((f) => ({
                              ...f,
                              is_active: e.target.value === 'active',
                            }))
                          }
                        >
                          <option value="active">Actif</option>
                          <option value="inactive">Inactif</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Commissions / Bonus</label>
                        <select
                          className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-sm"
                          value={createForm.commissions_enabled as string}
                          onChange={(e) =>
                            setCreateForm((f) => ({
                              ...f,
                              commissions_enabled: e.target.value,
                            }))
                          }
                        >
                          <option value="all">Tout</option>
                          <option value="yes">Avec</option>
                          <option value="no">Sans</option>
                        </select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium">Marques autorisées</label>

                      <div className="flex flex-wrap gap-3">
                        {brands.map((brand) => {
                          const marques = Array.isArray(createForm.marques) ? createForm.marques : [];
                          // Use normalize for comparison to handle case/format differences
                          const isChecked = marques.some(m => normalize(m) === normalize(brand.name));
                          return (
                            <label
                              key={brand.id}
                              className={cn(
                                "flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-all",
                                isChecked
                                  ? "bg-purple-100 dark:bg-purple-900/30 border-purple-500 text-purple-700 dark:text-purple-300"
                                  : "bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 hover:border-purple-300"
                              )}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) => {
                                  setCreateForm((f) => {
                                    const currentMarques = Array.isArray(f.marques) ? f.marques : [];
                                    const brandNorm = normalize(brand.name);

                                    if (e.target.checked) {
                                      // Add if not already present (normalized check)
                                      if (!currentMarques.some(m => normalize(m) === brandNorm)) {
                                        return { ...f, marques: [...currentMarques, brand.name] };
                                      }
                                      return f;
                                    } else {
                                      // Remove all matching variants (normalized check)
                                      return {
                                        ...f,
                                        marques: currentMarques.filter((m) => normalize(m) !== brandNorm),
                                      };
                                    }
                                  });
                                }}
                                className="sr-only"
                              />
                              <div className={cn(
                                "w-4 h-4 rounded border-2 flex items-center justify-center",
                                isChecked
                                  ? "bg-purple-500 border-purple-500"
                                  : "border-slate-400"
                              )}>
                                {isChecked && (
                                  <CheckCircle2 className="w-3 h-3 text-white" />
                                )}
                              </div>
                              <span className="text-sm font-medium">{brand.name}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}

            {activeCreateTab === 'entreprise' && (
              <div className="space-y-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/40 p-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-100">
                  <Building2 className="h-4 w-4 text-blue-500" />
                  <span>Informations société</span>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1 sm:col-span-2">
                    <label className="block text-sm font-medium mb-1">Nom de la société</label>
                    <Input
                      placeholder="Ex: Ma Société SARL"
                      value={createForm.company_name}
                      onChange={(e) => setCreateForm((f) => ({ ...f, company_name: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1 sm:col-span-2">
                    <label className="block text-sm font-medium mb-1">SIRET</label>
                    <Input
                      placeholder="Ex: 123 456 789 00012"
                      value={createForm.siret}
                      onChange={(e) => setCreateForm((f) => ({ ...f, siret: e.target.value }))}
                    />
                  </div>
                </div>

                {(() => {
                  const pappersQuery = createForm.siret;
                  if (!pappersQuery) return null;
                  const url = `https://www.pappers.fr/recherche?q=${encodeURIComponent(pappersQuery)}`;
                  return (
                    <div className="text-xs text-muted-foreground">
                      <a href={url} target="_blank" rel="noreferrer" className="underline">
                        Ouvrir la fiche sur pappers.fr
                      </a>
                    </div>
                  );
                })()}

                <div className="sm:col-span-2">
                  <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">Localisation</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium mb-1">Rue</label>
                        <Input
                          value={createForm.address}
                          onChange={(e) =>
                            setCreateForm((prev) => {
                              const next = { ...prev, address: e.target.value };
                              const addressComplete = buildAddressComplete(next.address, next.postal_code, next.city, next.country);
                              return { ...next, maps_link: buildMapsLink(addressComplete) };
                            })
                          }
                          placeholder="Ex: 44 Avenue Gambetta"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Code postal</label>
                        <Input
                          value={createForm.postal_code}
                          onChange={(e) =>
                            setCreateForm((prev) => {
                              const next = { ...prev, postal_code: e.target.value };
                              const addressComplete = buildAddressComplete(next.address, next.postal_code, next.city, next.country);
                              return { ...next, maps_link: buildMapsLink(addressComplete) };
                            })
                          }
                          placeholder="Ex: 17100"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Ville</label>
                        <Input
                          value={createForm.city}
                          onChange={(e) =>
                            setCreateForm((prev) => {
                              const next = { ...prev, city: e.target.value };
                              const addressComplete = buildAddressComplete(next.address, next.postal_code, next.city, next.country);
                              return { ...next, maps_link: buildMapsLink(addressComplete) };
                            })
                          }
                          placeholder="Ex: Saintes"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium mb-1">Adresse complète</label>
                        <Input
                          value={buildAddressComplete(createForm.address, createForm.postal_code, createForm.city, createForm.country)}
                          readOnly
                          className="bg-slate-50 dark:bg-slate-800/60"
                        />
                        {buildAddressComplete(createForm.address, createForm.postal_code, createForm.city, createForm.country) && (
                          <div className="mt-2 flex flex-wrap items-center gap-3">
                            <button
                              type="button"
                              onClick={() => setShowMapPreview((v) => !v)}
                              className={
                                "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors " +
                                (showMapPreview
                                  ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/40 dark:bg-blue-950/40 dark:text-blue-300'
                                  : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700')
                              }
                            >
                              <Map className="h-3.5 w-3.5" />
                              Carte
                            </button>
                            {createForm.maps_link && (
                              <a
                                href={createForm.maps_link}
                                target="_blank"
                                rel="noreferrer"
                                className="text-sm text-blue-600 hover:underline hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                              >
                                Ouvrir dans Google Maps
                              </a>
                            )}
                          </div>
                        )}
                        {showMapPreview && (
                          <div className="mt-3 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                            <iframe
                              title="Google Maps"
                              src={buildMapsEmbedSrc(buildAddressComplete(createForm.address, createForm.postal_code, createForm.city, createForm.country))}
                              className="w-full h-64"
                              referrerPolicy="no-referrer-when-downgrade"
                              allowFullScreen
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeCreateTab === 'bancaire' && (
              <div className="space-y-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/40 p-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-100">
                  <Briefcase className="h-4 w-4 text-emerald-500" />
                  <span>Informations bancaires</span>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1">
                    <label className="block text-sm font-medium mb-1">Banque</label>
                    <Input
                      placeholder="Nom de la banque"
                      value={createForm.bank_name}
                      onChange={(e) => setCreateForm((f) => ({ ...f, bank_name: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="block text-sm font-medium mb-1">Titulaire du compte</label>
                    <Input
                      placeholder="Nom du titulaire"
                      value={createForm.bank_account_holder}
                      onChange={(e) => setCreateForm((f) => ({ ...f, bank_account_holder: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="block text-sm font-medium mb-1">IBAN</label>
                    <Input
                      placeholder="FR76 3000 4000 5000 6000 7000 899"
                      value={createForm.iban}
                      onChange={(e) => setCreateForm((f) => ({ ...f, iban: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="block text-sm font-medium mb-1">BIC / SWIFT</label>
                    <Input
                      placeholder="Ex : PSSTFRPPXXX"
                      value={createForm.bic}
                      onChange={(e) => setCreateForm((f) => ({ ...f, bic: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
            )}

            {activeCreateTab === 'utilisateurs' && editingAdmin && (
              <div className="space-y-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/40 p-4">
                <AdminUsersManager adminId={editingAdmin.id} admin={editingAdmin} />
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4 border-t border-slate-200 dark:border-slate-700">
              <Button variant="outline" onClick={() => setShowCreate(false)} className="w-full sm:w-auto">Annuler</Button>
              <Button onClick={handleSaveAdmin} disabled={createAdmin.isPending || updateAdmin.isPending} className="w-full sm:w-auto">
                <Plus className="mr-2 h-4 w-4" />
                {editingAdmin ? 'Enregistrer' : 'Créer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div className="md:flex-1 md:min-h-0 md:overflow-auto">
        <div className="pt-2 sm:pt-4">
          {isLoading ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Chargement...</div>
          ) : admins.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Aucun {roleFilter === 'admin' ? 'administrateur' : 'apporteur'} pour le moment.</div>
          ) : viewMode === 'grid' ? (
            filteredAdmins.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">Aucun {roleFilter === 'admin' ? 'administrateur' : 'apporteur'} ne correspond à votre recherche.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-2 sm:gap-4 md:auto-rows-fr">
                {filteredAdmins.map((a, idx) => {
                  const fullName = [a.first_name, a.last_name].filter(Boolean).join(' ');
                  const initials = (fullName || a.email || 'A')
                    .split(' ')
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((p) => p[0]?.toUpperCase())
                    .join('');

                  const hasEmail = Boolean(a.email);
                  const hasPhone = Boolean((a as any).mobile_phone);
                  const hasAddress = Boolean(
                    (a as any).address ||
                    (a as any).city ||
                    (a as any).postal_code ||
                    (a as any).country,
                  );
                  const hasSiret = Boolean((a as any).siret);
                  const hasRib = Boolean(
                    (a as any).bank_name ||
                    (a as any).iban ||
                    (a as any).bic,
                  );

                  const isTypeSuperAdmin = a.role === 'super_admin';
                  const isTypeAdmin = a.role === 'admin';
                  const isTypeAdminOrSuper = isTypeSuperAdmin || isTypeAdmin;

                  return (
                    <div
                      key={a.id}
                      onClick={() => handleEdit(a)}
                      className="cursor-pointer group relative overflow-hidden rounded-xl sm:rounded-2xl border border-slate-200 dark:border-slate-700 bg-gradient-to-br from-white via-white to-slate-50/30 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800/30 hover:border-purple-400 dark:hover:border-purple-600 sm:hover:-translate-y-1 transition-all duration-300 ease-out animate-in fade-in slide-in-from-bottom-4 flex flex-col h-full"
                      style={{ animationDelay: `${idx * 50}ms`, animationFillMode: 'backwards' }}
                    >
                      {/* Header */}
                      <div className="relative px-3 sm:px-4 pt-3 sm:pt-4 pb-2 sm:pb-3 bg-gradient-to-r from-purple-50/50 via-transparent to-pink-50/50 dark:from-purple-950/20 dark:via-transparent dark:to-pink-950/20">
                        <div className="flex items-start justify-between gap-2 sm:gap-3 mb-2 sm:mb-3">
                          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                            <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0 text-purple-700 dark:text-purple-300 font-bold text-sm sm:text-lg">
                              {isTypeAdminOrSuper ? <Shield className="h-4 w-4 sm:h-6 sm:w-6" /> : initials}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-bold text-sm sm:text-base text-slate-900 dark:text-slate-100 truncate mb-0.5">
                                {fullName || '—'}
                              </h3>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={cn(
                                  "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase font-bold tracking-wide",
                                  isTypeSuperAdmin
                                    ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                    : isTypeAdmin
                                      ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400'
                                      : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                                )}>
                                  {isTypeSuperAdmin ? 'Super Admin' : isTypeAdmin ? 'Administrateur' : 'Apporteur'}
                                </span>
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase font-bold tracking-wide ${a.is_active
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                  : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                  }`}>
                                  {a.is_active ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                                  {a.is_active ? 'Actif' : 'Inactif'}
                                </span>
                                {!isTypeAdminOrSuper && a.commissions_enabled && a.commissions_enabled !== 'no' && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-blue-100 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300 border border-blue-200">
                                    <Target className="h-3 w-3" />
                                    {a.commissions_enabled === 'all' ? 'Tout' : 'Commissions'}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Body */}
                      <div className="px-3 sm:px-4 py-2 sm:py-3 space-y-1.5 sm:space-y-2 flex-1">
                        <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm">
                          <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-md sm:rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                            <Mail className={`h-3 w-3 sm:h-3.5 sm:w-3.5 ${hasEmail ? 'text-emerald-500 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`} />
                          </div>
                          <span className={`truncate ${hasEmail ? 'text-slate-600 dark:text-slate-400' : 'text-slate-400 italic'}`}>
                            {hasEmail ? a.email : 'Non renseigné'}
                          </span>
                        </div>

                        <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm">
                          <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-md sm:rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                            <Phone className={`h-3 w-3 sm:h-3.5 sm:w-3.5 ${hasPhone ? 'text-emerald-500 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`} />
                          </div>
                          <span className={`truncate ${hasPhone ? 'text-slate-600 dark:text-slate-400' : 'text-slate-400 italic'}`}>
                            {hasPhone ? (a as any).mobile_phone : 'Non renseigné'}
                          </span>
                        </div>

                        <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm">
                          <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-md sm:rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                            <MapPin className={`h-3 w-3 sm:h-3.5 sm:w-3.5 ${hasAddress ? 'text-emerald-500 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`} />
                          </div>
                          <span className={`truncate ${hasAddress ? 'text-slate-600 dark:text-slate-400' : 'text-slate-400 italic'}`}>
                            {[a.city, a.country].filter(Boolean).join(', ') || 'Non renseigné'}
                          </span>
                        </div>

                        <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm">
                          <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-md sm:rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                            <Clock className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-blue-500" />
                          </div>
                          <span className="truncate text-slate-600 dark:text-slate-400">
                            {a.last_login
                              ? new Date(a.last_login).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' + new Date(a.last_login).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                              : <span className="italic text-slate-400">Jamais connecté</span>
                            }
                          </span>
                        </div>

                        {a.role !== 'admin' && (
                          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                            <div className="flex gap-2 flex-wrap">
                              <span className={`text-[10px] px-2 py-0.5 rounded-full border ${hasSiret ? 'bg-slate-50 border-slate-200 text-slate-600 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300' : 'bg-slate-50 border-slate-100 text-slate-300 dark:bg-slate-900 dark:border-slate-800 dark:text-slate-600'}`}>
                                SIRET
                              </span>
                              <span className={`text-[10px] px-2 py-0.5 rounded-full border ${hasRib ? 'bg-slate-50 border-slate-200 text-slate-600 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300' : 'bg-slate-50 border-slate-100 text-slate-300 dark:bg-slate-900 dark:border-slate-800 dark:text-slate-600'}`}>
                                RIB
                              </span>
                              {a.marques && a.marques.length > 0 && a.marques.map((m) => (
                                <span key={m} className="text-[10px] px-2 py-0.5 rounded-full border bg-purple-50 border-purple-200 text-purple-600 dark:bg-purple-900/30 dark:border-purple-700 dark:text-purple-300">
                                  {getBrandDisplayName(m)}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Footer Actions */}
                      <div className="mt-auto px-2 sm:px-3 py-2 sm:py-3 bg-slate-50/50 dark:bg-slate-800/30 border-t border-slate-200/50 dark:border-slate-700/50">
                        <div className="flex items-center justify-end gap-1.5 sm:gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleStatus(a);
                            }}
                            className={cn(
                              "flex items-center justify-center w-8 h-8 rounded-lg transition-all shadow-sm",
                              a.is_active
                                ? "bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400 hover:bg-amber-100"
                                : "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400 hover:bg-emerald-100"
                            )}
                            title={a.is_active ? "Désactiver" : "Activer"}
                          >
                            {a.is_active ? <XCircle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEdit(a); }}
                            className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400 hover:bg-blue-100 hover:scale-105 transition-all shadow-sm"
                            title="Modifier"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setConfirmDeleteId(a.id);
                            }}
                            disabled={a.users_count > 0 || deleteAdmin.isPending}
                            className="flex items-center justify-center w-8 h-8 rounded-lg bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400 hover:bg-red-100 hover:scale-105 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            title={isTypeAdmin ? "Supprimer l'administrateur" : "Supprimer l'apporteur"}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          ) : viewMode === 'list' ? (
            filteredAdmins.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">Aucun {roleFilter === 'admin' ? 'administrateur' : 'apporteur'} ne correspond à votre recherche.</div>
            ) : (
              <div className="space-y-2 sm:space-y-3">
                {filteredAdmins.map((a, idx) => {
                  const fullName = [a.first_name, a.last_name].filter(Boolean).join(' ');
                  const initials = (fullName || a.email || 'A')
                    .split(' ')
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((p) => p[0]?.toUpperCase())
                    .join('');
                  const hasEmail = Boolean(a.email);
                  const hasPhone = Boolean((a as any).mobile_phone);
                  const hasAddress = Boolean(
                    (a as any).address ||
                    (a as any).city ||
                    (a as any).postal_code ||
                    (a as any).country,
                  );

                  return (
                    <div
                      key={a.id}
                      onClick={() => handleEdit(a)}
                      className="cursor-pointer flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-3 sm:p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:shadow-lg hover:border-purple-300 dark:hover:border-purple-700 sm:hover:translate-x-1 transition-all duration-300 ease-out animate-in fade-in slide-in-from-left-4"
                      style={{ animationDelay: `${idx * 30}ms`, animationFillMode: 'backwards' }}
                    >
                      <div className="flex items-center gap-2 sm:gap-4">
                        <div className="w-9 h-9 sm:w-12 sm:h-12 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center flex-shrink-0 text-purple-700 dark:text-purple-300 font-bold text-sm sm:text-base">
                          {a.role === 'admin' || a.role === 'super_admin' ? <Shield className="h-4 w-4 sm:h-6 sm:w-6" /> : initials}
                        </div>
                        <div className="flex-1 min-w-0 sm:hidden">
                          <h3 className="font-bold text-sm text-slate-900 dark:text-slate-100 truncate">{fullName || '—'}</h3>
                          <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                            <span className={cn(
                              "inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] uppercase font-bold tracking-wide",
                              a.role === 'super_admin'
                                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                : a.role === 'admin'
                                  ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400'
                                  : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                            )}>
                              {a.role === 'super_admin' ? 'Super Admin' : a.role === 'admin' ? 'Admin' : 'Apporteur'}
                            </span>
                            <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] uppercase font-bold tracking-wide ${a.is_active
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                              }`}>
                              {a.is_active ? <CheckCircle2 className="h-2.5 w-2.5" /> : <XCircle className="h-2.5 w-2.5" />}
                              {a.is_active ? 'Actif' : 'Inactif'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0 hidden sm:block">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-bold text-slate-900 dark:text-slate-100">{fullName || '—'}</h3>
                          <span className={cn(
                            "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase font-bold tracking-wide",
                            a.role === 'super_admin'
                              ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                              : a.role === 'admin'
                                ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400'
                                : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                          )}>
                            {a.role === 'super_admin' ? 'Super Admin' : a.role === 'admin' ? 'Administrateur' : 'Apporteur'}
                          </span>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase font-bold tracking-wide ${a.is_active
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                            }`}>
                            {a.is_active ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                            {a.is_active ? 'Actif' : 'Inactif'}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-600 dark:text-slate-400 flex-wrap">
                          <span className={`flex items-center gap-1 ${!hasEmail ? 'italic text-slate-400' : ''}`}>
                            <Mail className={`h-3 w-3 ${hasEmail ? 'text-emerald-500 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`} />
                            {hasEmail ? a.email : 'Non renseigné'}
                          </span>
                          <span className={`flex items-center gap-1 ${!hasPhone ? 'italic text-slate-400' : ''}`}>
                            <Phone className={`h-3 w-3 ${hasPhone ? 'text-emerald-500 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`} />
                            {hasPhone ? (a as any).mobile_phone : 'Non renseigné'}
                          </span>
                          <span className={`flex items-center gap-1 ${!hasAddress ? 'italic text-slate-400' : ''}`}>
                            <MapPin className={`h-3 w-3 ${hasAddress ? 'text-emerald-500 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`} />
                            {[a.city, a.country].filter(Boolean).join(', ') || 'Non renseigné'}
                          </span>
                          <span className="flex items-center gap-1 text-slate-500 dark:text-slate-400">
                            {a.last_login
                              ? new Date(a.last_login).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' + new Date(a.last_login).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                              : <span className="italic text-slate-400">Jamais connecté</span>
                            }
                          </span>
                        </div>
                      </div>
                      {/* Mobile info rows */}
                      <div className="sm:hidden space-y-1 text-xs text-slate-600 dark:text-slate-400 pl-1">
                        <div className={`flex items-center gap-1.5 ${!hasEmail ? 'italic text-slate-400' : ''}`}>
                          <Mail className={`h-3 w-3 flex-shrink-0 ${hasEmail ? 'text-emerald-500' : 'text-red-500'}`} />
                          <span className="truncate">{hasEmail ? a.email : 'Non renseigné'}</span>
                        </div>
                        <div className={`flex items-center gap-1.5 ${!hasPhone ? 'italic text-slate-400' : ''}`}>
                          <Phone className={`h-3 w-3 flex-shrink-0 ${hasPhone ? 'text-emerald-500' : 'text-red-500'}`} />
                          <span className="truncate">{hasPhone ? (a as any).mobile_phone : 'Non renseigné'}</span>
                        </div>
                        <div className={`flex items-center gap-1.5 ${!hasAddress ? 'italic text-slate-400' : ''}`}>
                          <MapPin className={`h-3 w-3 flex-shrink-0 ${hasAddress ? 'text-emerald-500' : 'text-red-500'}`} />
                          <span className="truncate">{[a.city, a.country].filter(Boolean).join(', ') || 'Non renseigné'}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-slate-500">
                          <span className="truncate">
                            {a.last_login
                              ? new Date(a.last_login).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' + new Date(a.last_login).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                              : <span className="italic text-slate-400">Jamais connecté</span>
                            }
                          </span>
                        </div>
                      </div>
                      {/* Actions */}
                      <div className="flex gap-1 sm:gap-2 justify-end sm:justify-start border-t sm:border-t-0 border-slate-100 dark:border-slate-800 pt-2 sm:pt-0">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleStatus(a);
                          }}
                          className={cn(
                            "p-1.5 sm:p-2 rounded-lg transition-colors",
                            a.is_active
                              ? "text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/30"
                              : "text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
                          )}
                          title={a.is_active ? "Désactiver" : "Activer"}
                        >
                          {a.is_active ? <XCircle className="h-4 w-4 sm:h-5 sm:w-5" /> : <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5" />}
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleEdit(a); }}
                          className="p-1.5 sm:p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/30 rounded-lg transition-colors"
                          title="Modifier"
                        >
                          <Edit className="h-4 w-4 sm:h-5 sm:w-5" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmDeleteId(a.id);
                          }}
                          disabled={a.users_count > 0 || deleteAdmin.isPending}
                          className="p-1.5 sm:p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors disabled:opacity-50"
                          title={a.role === 'admin' ? "Supprimer l'administrateur" : "Supprimer l'apporteur"}
                        >
                          <Trash2 className="h-4 w-4 sm:h-5 sm:w-5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          ) : (
            <div className="bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700 shadow-lg overflow-hidden">
              <div className="overflow-x-auto overflow-y-auto">
                <Table className="w-full min-w-[800px]">
                  <TableHeader className="bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20 border-b-2 border-blue-200 dark:border-blue-800">
                    <TableRow>
                      <TableHead
                        className="min-w-[120px] px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors select-none"
                        onClick={() => handleSort('last_name')}
                      >
                        Nom <SortIcon column="last_name" sortColumn={sortColumn} sortDirection={sortDirection} />
                      </TableHead>
                      <TableHead
                        className="min-w-[120px] px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors select-none"
                        onClick={() => handleSort('first_name')}
                      >
                        Prénom <SortIcon column="first_name" sortColumn={sortColumn} sortDirection={sortDirection} />
                      </TableHead>
                      <TableHead
                        className="min-w-[200px] px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors select-none"
                        onClick={() => handleSort('email')}
                      >
                        Email <SortIcon column="email" sortColumn={sortColumn} sortDirection={sortDirection} />
                      </TableHead>
                      <TableHead
                        className="min-w-[120px] px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors select-none"
                        onClick={() => handleSort('mobile_phone')}
                      >
                        Téléphone <SortIcon column="mobile_phone" sortColumn={sortColumn} sortDirection={sortDirection} />
                      </TableHead>
                      <TableHead
                        className="min-w-[140px] px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors select-none"
                        onClick={() => handleSort('city')}
                      >
                        Ville <SortIcon column="city" sortColumn={sortColumn} sortDirection={sortDirection} />
                      </TableHead>
                      {defaultRoleFilter !== 'admin' && (
                        <TableHead className="min-w-[140px] px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">SIRET / RIB</TableHead>
                      )}
                      <TableHead
                        className="min-w-[120px] px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors select-none"
                        onClick={() => handleSort('role')}
                      >
                        Rôle <SortIcon column="role" sortColumn={sortColumn} sortDirection={sortDirection} />
                      </TableHead>
                      {defaultRoleFilter !== 'admin' && (
                        <TableHead className="min-w-[120px] px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Marques</TableHead>
                      )}
                      <TableHead
                        className="min-w-[80px] px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors select-none"
                        onClick={() => handleSort('is_active')}
                      >
                        Statut <SortIcon column="is_active" sortColumn={sortColumn} sortDirection={sortDirection} />
                      </TableHead>
                      <TableHead className="min-w-[140px] px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                        Dernière Connexion
                      </TableHead>
                      <TableHead
                        className="sticky right-0 min-w-[120px] px-4 py-3 text-right text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20 border-l border-blue-200/70 dark:border-blue-800/70 z-10"
                      >
                        Actions
                      </TableHead>
                    </TableRow>
                    <TableRow className="bg-slate-100/50 dark:bg-slate-800/50">
                      <TableHead className="py-2 px-2">
                        <input
                          type="text"
                          placeholder="Filtrer..."
                          value={columnFilters.name}
                          onChange={(e) => setColumnFilters({ ...columnFilters, name: e.target.value })}
                          className="w-full px-2 py-1 text-xs border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800"
                        />
                      </TableHead>
                      <TableHead className="py-2 px-2">
                        <input
                          type="text"
                          placeholder="Filtrer..."
                          value={columnFilters.name}
                          onChange={(e) => setColumnFilters({ ...columnFilters, name: e.target.value })}
                          className="w-full px-2 py-1 text-xs border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800"
                        />
                      </TableHead>
                      <TableHead className="py-2 px-2">
                        <input
                          type="text"
                          placeholder="Filtrer..."
                          value={columnFilters.email}
                          onChange={(e) => setColumnFilters({ ...columnFilters, email: e.target.value })}
                          className="w-full px-2 py-1 text-xs border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800"
                        />
                      </TableHead>
                      <TableHead className="py-2 px-2">
                        <input
                          type="text"
                          placeholder="Filtrer..."
                          value={columnFilters.phone}
                          onChange={(e) => setColumnFilters({ ...columnFilters, phone: e.target.value })}
                          className="w-full px-2 py-1 text-xs border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800"
                        />
                      </TableHead>
                      <TableHead className="py-2 px-2">
                        <input
                          type="text"
                          placeholder="Filtrer..."
                          value={columnFilters.city}
                          onChange={(e) => setColumnFilters({ ...columnFilters, city: e.target.value })}
                          className="w-full px-2 py-1 text-xs border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800"
                        />
                      </TableHead>
                      {defaultRoleFilter !== 'admin' && (
                        <TableHead className="py-2 px-2">
                          <input
                            type="text"
                            placeholder="Filtrer..."
                            value={columnFilters.siret}
                            onChange={(e) => setColumnFilters({ ...columnFilters, siret: e.target.value })}
                            className="w-full px-2 py-1 text-xs border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800"
                          />
                        </TableHead>
                      )}
                      <TableHead className="py-2 px-2">
                        <select
                          value={columnFilters.role}
                          onChange={(e) => setColumnFilters({ ...columnFilters, role: e.target.value })}
                          className="w-full px-2 py-1 text-xs border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800"
                        >
                          <option value="">Tous</option>
                          {defaultRoleFilter === 'admin' ? (
                            <>
                              <option value="admin">Admin</option>
                              <option value="super_admin">Super Admin</option>
                            </>
                          ) : (
                            <>
                              <option value="admin">Admin</option>
                              <option value="apporteur">Apporteur</option>
                            </>
                          )}
                        </select>
                      </TableHead>
                      {defaultRoleFilter !== 'admin' && (
                        <TableHead className="py-2 px-2">
                          <select
                            value={columnFilters.brand}
                            onChange={(e) => setColumnFilters({ ...columnFilters, brand: e.target.value })}
                            className="w-full px-2 py-1 text-xs border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800"
                          >
                            <option value="">Toutes</option>
                            {brands.map((brand) => (
                              <option key={brand.id} value={brand.name}>
                                {brand.name}
                              </option>
                            ))}
                          </select>
                        </TableHead>
                      )}
                      <TableHead className="py-2 px-2">
                        <select
                          value={columnFilters.status}
                          onChange={(e) => setColumnFilters({ ...columnFilters, status: e.target.value })}
                          className="w-full px-2 py-1 text-xs border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800"
                        >
                          <option value="">Tous</option>
                          <option value="actif">Actif</option>
                          <option value="inactif">Inactif</option>
                        </select>
                      </TableHead>
                      <TableHead className="py-2 px-2"></TableHead>
                      <TableHead className="sticky right-0 py-2 px-2 bg-slate-100/50 dark:bg-slate-800/50 border-l border-blue-200/70 dark:border-blue-800/70"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody className="divide-y divide-slate-200 dark:divide-slate-700">
                    {sortedAdmins.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={defaultRoleFilter === 'admin' ? 9 : 11} className="text-center py-8 text-slate-500 dark:text-slate-400">
                          <div className="flex flex-col items-center gap-2">
                            <Users className="h-8 w-8 opacity-50" />
                            <span>Aucun résultat trouvé</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : sortedAdmins.map((a) => {
                      const hasSiret = Boolean(a.siret);
                      const hasRib = Boolean(a.bank_name || a.iban || a.bic);

                      return (
                        <Fragment key={a.id}>
                          <TableRow
                            onClick={() => handleEdit(a)}
                            className="hover:bg-blue-50/50 dark:hover:bg-blue-950/20 transition-colors duration-200 cursor-pointer"
                          >
                            <TableCell>{a.last_name || '—'}</TableCell>
                            <TableCell>{a.first_name || '—'}</TableCell>
                            <TableCell>{a.email}</TableCell>
                            <TableCell>{a.mobile_phone || '—'}</TableCell>
                            <TableCell>
                              {[a.city, a.country].filter(Boolean).join(', ') || '—'}
                            </TableCell>
                            {defaultRoleFilter !== 'admin' && (
                              <TableCell>
                                {a.role === 'admin' ? '—' : (
                                  <div className="flex items-center gap-2">
                                    <span
                                      className="inline-flex items-center"
                                      title={hasSiret ? 'SIRET renseigné' : 'SIRET manquant'}
                                    >
                                      {hasSiret ? (
                                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                                      ) : (
                                        <XCircle className="h-4 w-4 text-red-500" />
                                      )}
                                    </span>
                                    <span
                                      className="inline-flex items-center"
                                      title={hasRib ? 'RIB renseigné' : 'RIB manquant'}
                                    >
                                      {hasRib ? (
                                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                                      ) : (
                                        <XCircle className="h-4 w-4 text-red-500" />
                                      )}
                                    </span>
                                  </div>
                                )}
                              </TableCell>
                            )}
                            <TableCell>
                              <span className={cn(
                                "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase font-bold tracking-wide",
                                a.role === 'super_admin'
                                  ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                  : a.role === 'admin'
                                    ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400'
                                    : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                              )}>
                                {a.role === 'super_admin' ? 'Super Admin' : a.role === 'admin' ? 'Administrateur' : 'Apporteur'}
                              </span>
                            </TableCell>
                            {defaultRoleFilter !== 'admin' && (
                              <TableCell>
                                {a.role === 'apporteur' && a.marques && a.marques.length > 0 ? (
                                  <div className="flex flex-wrap gap-1">
                                    {a.marques.map((m) => (
                                      <span key={m} className="text-[10px] px-2 py-0.5 rounded-full bg-purple-50 border border-purple-200 text-purple-600 dark:bg-purple-900/30 dark:border-purple-700 dark:text-purple-300">
                                        {getBrandDisplayName(m)}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-slate-400">—</span>
                                )}
                              </TableCell>
                            )}
                            <TableCell>
                              <span
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase font-bold tracking-wide ${a.is_active
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                  : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                                  }`}
                              >
                                {a.is_active ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                                {a.is_active ? 'Actif' : 'Inactif'}
                              </span>
                            </TableCell>
                            <TableCell className="text-sm text-slate-600 dark:text-slate-400">
                              {a.last_login
                                ? new Date(a.last_login).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' + new Date(a.last_login).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                                : <span className="italic text-slate-400">Jamais connecté</span>
                              }
                            </TableCell>
                            <TableCell className="sticky right-0 min-w-[120px] bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-700 z-10">
                              <div className="flex items-center justify-end gap-1.5">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleStatus(a);
                                  }}
                                  className={cn(
                                    "p-2 rounded-lg transition-colors",
                                    a.is_active
                                      ? "text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/30"
                                      : "text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
                                  )}
                                  title={a.is_active ? "Désactiver" : "Activer"}
                                >
                                  {a.is_active ? <XCircle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setConfirmDeleteId(a.id);
                                  }}
                                  disabled={a.users_count > 0 || deleteAdmin.isPending}
                                  className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors disabled:opacity-50"
                                  title={a.role === 'admin' ? "Supprimer l'administrateur" : "Supprimer l'apporteur"}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </TableCell>
                          </TableRow>
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
          <ConfirmDialog
            open={confirmDeleteId !== null}
            onOpenChange={(open) => {
              if (!open) setConfirmDeleteId(null);
            }}
            title={(() => {
              if (!confirmDeleteId) return "Suppression";
              const a = admins.find(x => x.id === confirmDeleteId);
              return a?.role === 'admin' ? "Supprimer l'administrateur" : "Supprimer l'apporteur";
            })()}
            description={(() => {
              if (!confirmDeleteId) return "";
              const a = admins.find(x => x.id === confirmDeleteId);
              return a?.role === 'admin'
                ? "Cette action est irréversible. La suppression d'un administrateur est définitive."
                : "Cette action est irréversible. Vous ne pouvez pas supprimer un apporteur s'il possède encore des utilisateurs.";
            })()}
            confirmText="Supprimer"
            isLoading={deleteAdmin.isPending}
            onConfirm={async () => {
              if (confirmDeleteId == null) return;
              const targetAdmin = admins.find(a => a.id === confirmDeleteId);
              const isTypeAdmin = targetAdmin?.role === 'admin';
              try {
                await deleteAdmin.mutateAsync(confirmDeleteId);
                setConfirmDeleteId(null);
                showSuccess(isTypeAdmin ? 'Administrateur supprimé avec succès' : 'Apporteur supprimé avec succès');
              } catch (e: any) {
                showError(e?.response?.data?.detail || 'Suppression impossible');
              }
            }}
          />
        </div>
      </div>
    </div >
  );
}
